import React from "react";
import { Box, Container, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { motion } from "framer-motion";
import { FormattedMessage } from "react-intl";
import {
  Shield,
  DollarSign,
  Infinity,
  Target,
  Users,
} from "lucide-react";
import { useSelector } from "react-redux";

const WhyChooseUsSection = () => {
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const usps = [
    {
      icon: Shield,
      titleId: "why_choose_us.usp.master_recruitment.title",
      descriptionId: "why_choose_us.usp.master_recruitment.description",
    },
    {
      icon: DollarSign,
      titleId: "why_choose_us.usp.fixed_cost.title",
      descriptionId: "why_choose_us.usp.fixed_cost.description",
    },
    {
      icon: Infinity,
      titleId: "why_choose_us.usp.unlimited_recruitment.title",
      descriptionId: "why_choose_us.usp.unlimited_recruitment.description",
    },
    {
      icon: Target,
      titleId: "why_choose_us.usp.specialized_platform.title",
      descriptionId: "why_choose_us.usp.specialized_platform.description",
    },
    {
      icon: Users,
      titleId: "why_choose_us.usp.hybrid_approach.title",
      descriptionId: "why_choose_us.usp.hybrid_approach.description",
    },
  ];

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: "3rem", md: "5rem" },
        px: { xs: "1rem", sm: "1.5rem", lg: "2rem" },
        bgcolor: "hsl(178 93% 10%)",
        color: theme?.palette.common.white,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: "25rem",
          height: "25rem",
          borderRadius: "9999px",
          bgcolor: "hsl(153 68% 25% / 0.1)",
          filter: "blur(120px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "18.75rem",
          height: "18.75rem",
          borderRadius: "9999px",
          bgcolor: "hsl(72 93% 52% / 0.05)",
          filter: "blur(100px)",
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative" }}>
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          sx={{ textAlign: "center", mb: "3.5rem" }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              px: "1.25rem",
              py: "0.5rem",
              borderRadius: "9999px",
              bgcolor: "hsl(153 68% 25% / 0.2)",
              border: "1px solid hsl(153 68% 25% / 0.3)",
              color: "hsl(153 68% 40%)",
              fontSize: "0.875rem",
              fontWeight: 700,
              mb: "1.5rem",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
            }}
          >
            <Box
              component="span"
              sx={{
                width: "0.5rem",
                height: "0.5rem",
                borderRadius: "9999px",
                bgcolor: "hsl(153 68% 25%)",
              }}
            />
            <FormattedMessage id="why_choose_us.badge.enterprise_space" />
          </Box>

          <Box
            component="h2"
            sx={{
              m: 0,
              fontSize: { xs: "1.875rem", md: "2.25rem", lg: "3rem" },
              lineHeight: 1.15,
              fontWeight: 900,
              fontFamily: "'Segoe UI', 'Work Sans', sans-serif",
            }}
          >
            <FormattedMessage id="why_choose_us.heading.line1" />
            <br />
            <Box component="span" sx={{ color: "hsl(153 68% 40%)" }}>
              <FormattedMessage id="why_choose_us.heading.line2" />
            </Box>
          </Box>

          <Box
            component="p"
            sx={{
              mt: "1.25rem",
              mb: 0,
              mx: "auto",
              maxWidth: "42rem",
              color: alpha(theme?.palette.common.white, 0.6),
              fontSize: "1.125rem",
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            }}
          >
            <FormattedMessage id="why_choose_us.description" />
          </Box>

          <Box
            component="a"
            href={i18nLocale === "nl"
              ? "https://meetings-eu1.hubspot.com/meetings/jules-fadie"
              : i18nLocale === "fr-fr"
                ? "https://info.squarehub.eu/meetings/felix-dessirier?uuid=7c4153b4-d629-4388-9ac7-306ef7f0879b"
                : "https://meetings-eu1.hubspot.com/meetings/sebastien-del-marmol"}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "0.5rem",
              mt: "1.5rem",
              px: "2rem",
              py: "0.875rem",
              borderRadius: "0.75rem",
              bgcolor: "hsl(153 68% 25%)",
              color: theme?.palette.common.white,
              textDecoration: "none",
              fontSize: "0.875rem",
              fontWeight: 700,
              transition: "background-color 0.2s ease",
              fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
              letterSpacing: "0.01em",
              "&:hover": {
                bgcolor: "hsl(153 68% 25% / 0.9)",
              },
            }}
          >
            <FormattedMessage id="why_choose_us.contact_button" />
          </Box>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: "1.5rem",
          }}
        >
          {usps.map((usp, i) => (
            <Box
              key={usp.titleId}
              component={motion.div}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              sx={{
                p: "1.75rem",
                borderRadius: "1rem",
                bgcolor: "hsl(0 0% 100% / 0.05)",
                border: "1px solid hsl(0 0% 100% / 0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  borderColor: "hsl(153 68% 25% / 0.4)",
                  bgcolor: "hsl(0 0% 100% / 0.1)",
                },
              }}
            >
              <Box
                sx={{
                  width: "3rem",
                  height: "3rem",
                  borderRadius: "0.75rem",
                  bgcolor: "hsl(153 68% 25% / 0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: "1.25rem",
                }}
              >
                <usp.icon size={22} color="hsl(153 68% 40%)" />
              </Box>
              <Box component="h3" sx={{ m: 0, mb: "0.5rem", fontSize: "1.125rem", fontWeight: 700, lineHeight: "1.76rem", fontFamily: "Segoe UI Semibold, system-ui, sans-serif" }}>
                <FormattedMessage id={usp.titleId} />
              </Box>
              <Box component="p" sx={{ m: 0, color: alpha(theme?.palette.common.white, 0.5), fontSize: "0.875rem", lineHeight: 1.6, fontFamily: "Segoe UI Regular, system-ui, sans-serif" }}>
                <FormattedMessage id={usp.descriptionId} />
              </Box>
            </Box>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default WhyChooseUsSection;